import clsx from "clsx";

import LogoFull from "../assets/logo-full.svg?react";
import { ROUTES } from "../routes";

import { WIP_ENABLED_USE_CASES } from "./constants";
import { Youtube, Linkedin } from "./icons/brandIcons";
import Link from "./Link";

export default function Footer() {
  const gridColumns = WIP_ENABLED_USE_CASES
    ? "lg:grid-cols-5"
    : "lg:grid-cols-4";
  return (
    <footer className="w-full border-t bg-white/70 backdrop-blur-sm dark:bg-black/40">
      <div className="mx-auto max-w-[1600px] px-4 py-8 sm:py-12 md:px-8 md:py-16 lg:px-16">
        <div
          className={clsx(
            "grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-8 lg:gap-12",
            gridColumns,
          )}
        >
          {/* Column 1: Brand */}
          <div className="lg:col-span-1">
            <LogoFull className="text-[#0247FE] dark:text-white" />
            <div className="mt-6 flex items-center gap-3">
              {/* YouTube */}
              <a
                href="https://www.youtube.com/@flowstep_ai"
                aria-label="YouTube"
                className="hover:bg-accent hover:border-foreground/20 focus-visible:ring-primary flex size-10 items-center justify-center rounded-md border transition-all duration-200 hover:scale-105 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
              >
                <Youtube className="size-5" />
              </a>

              {/* X (Twitter) */}
              <a
                href="https://x.com/flowstep_ai"
                aria-label="X (Twitter)"
                className="hover:bg-accent hover:border-foreground/20 focus-visible:ring-primary flex size-10 items-center justify-center rounded-md border transition-all duration-200 hover:scale-105 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
              >
                <svg className="size-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>

              {/* LinkedIn */}
              <a
                href="https://www.linkedin.com/company/flowstep-ai/"
                aria-label="LinkedIn"
                className="hover:bg-accent hover:border-foreground/20 focus-visible:ring-primary flex size-10 items-center justify-center rounded-md border transition-all duration-200 hover:scale-105 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
              >
                <Linkedin className="size-5" />
              </a>
            </div>
          </div>

          {/* Column 2: Product */}
          <div className="lg:col-span-1">
            <h3 className="mb-4 text-base font-medium">Product</h3>
            <ul className="space-y-2">
              <FooterLink href={ROUTES.mcp}>MCP server</FooterLink>
              <FooterLink href={ROUTES.changelog} external>
                Changelog
              </FooterLink>
              <FooterLink href={ROUTES.pricing}>Pricing</FooterLink>
              <FooterLink href={ROUTES.affiliates}>Affiliates</FooterLink>
            </ul>
          </div>

          {/* Column 3: Explore */}
          {WIP_ENABLED_USE_CASES ? (
            <div className="lg:col-span-1">
              <h3 className="mb-4 text-base font-medium">Explore</h3>
              <ul className="space-y-2">
                <FooterLink href={ROUTES.founders}>Founders</FooterLink>
                <FooterLink href={ROUTES.useCases}>Product Managers</FooterLink>
                <FooterLink href={ROUTES.designers}>Designers</FooterLink>
                <FooterLink href={ROUTES.marketers}>Marketers</FooterLink>
                <FooterLink href={ROUTES.sales}>Sales</FooterLink>
              </ul>
            </div>
          ) : null}

          {/* Column 4: Resources */}
          <div className="lg:col-span-1">
            <h3 className="mb-4 text-base font-medium">Resources</h3>
            <ul className="space-y-2">
              <FooterLink href={ROUTES.blog}>Blog</FooterLink>
              <FooterLink href={ROUTES.docs} external>
                Docs
              </FooterLink>
              <FooterLink href={ROUTES.about}>About</FooterLink>
              <FooterLink href={ROUTES.pressKit}>Press kit</FooterLink>
            </ul>
          </div>

          {/* Column 5: Legal */}
          <div className="lg:col-span-1">
            <h3 className="mb-4 text-base font-medium">Legal</h3>
            <ul className="space-y-2">
              <FooterLink href={ROUTES.privacyPolicy}>
                Privacy Policy
              </FooterLink>
              <FooterLink href={ROUTES.termsOfUse}>Terms of Use</FooterLink>
              <FooterLink href={ROUTES.cookiePolicy}>Cookie Policy</FooterLink>
              <FooterLink href={ROUTES.affiliateProgramTerms}>
                Affiliate Program
              </FooterLink>
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
}

type FooterLinkProps = {
  href: string;
  children: React.ReactNode;
  external?: boolean;
};

function FooterLink({ href, children, external }: FooterLinkProps) {
  return (
    <li>
      <Link
        className="text-muted-foreground hover:text-foreground focus-visible:text-foreground text-sm underline-offset-4 transition-colors hover:underline focus-visible:underline focus-visible:outline-none"
        href={href}
        external={external}
      >
        {children}
      </Link>
    </li>
  );
}
